/*
 * KNS scoped 3D entry controls for the default_product_v1-1 product template.
 * Drives the theme's existing <media-gallery> / <product-model> media only.
 * No third-party viewer is created; all interaction uses public DOM APIs.
 */
(() => {
  const DEFAULT_TIMEOUT = 8000;
  const UI_POLL_INTERVAL = 100;
  const UI_POLL_TRIES = 20; // bounded 2s poll for Shopify's native model viewer UI

  const escapeId = (value) => (window.CSS && CSS.escape
    ? CSS.escape(value)
    : String(value).replace(/[^\w-]/g, '\\$&'));

  const reducedMotion = () => Boolean(window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  class KnsProduct3dControls extends HTMLElement {
    constructor() {
      super();
      this.state = 'idle';
      this.generation = 0;
      this.rotating = false;
      this.viewer = null;
      this.timers = new Set();
      this.observer = null;
      this.attemptAbort = null;
      this.connectionAbort = null;

      this.handleClick = (event) => {
        const target = event.target;
        const button = target && target.closest ? target.closest('button') : null;
        if (!button || !this.contains(button)) return;
        if (button === this.openBtn) {
          this.activate();
        } else if (button.dataset.angle) {
          this.applyAngle(button);
        } else if (button.dataset.rotate) {
          this.toggleRotate(button);
        }
      };
    }

    connectedCallback() {
      this.openBtn = this.querySelector('.kns-3d__open');
      this.panel = this.querySelector('.kns-3d__controls');
      this.statusEl = this.querySelector('.kns-3d__status');
      this.rotateBtn = this.querySelector('[data-rotate]');
      if (this.connectionAbort) return; // already bound; exactly one handler per instance
      this.connectionAbort = new AbortController();
      this.addEventListener('click', this.handleClick, { signal: this.connectionAbort.signal });
      if (this.rotateBtn && reducedMotion()) this.rotateBtn.hidden = true;
    }

    disconnectedCallback() {
      this.cancelAttempt();
      this.stopRotation();
      if (this.connectionAbort) this.connectionAbort.abort();
      this.connectionAbort = null;
      this.viewer = null;
      this.state = 'idle';
      if (this.panel) this.panel.hidden = true;
    }

    get timeout() {
      const custom = parseInt(this.dataset.timeout, 10);
      return Number.isFinite(custom) && custom > 0 ? custom : DEFAULT_TIMEOUT;
    }

    /* ---------------- lifecycle helpers ---------------- */

    setTimer(fn, delay) {
      const id = setTimeout(() => {
        this.timers.delete(id);
        fn();
      }, delay);
      this.timers.add(id);
      return id;
    }

    clearTimers() {
      this.timers.forEach((id) => clearTimeout(id));
      this.timers.clear();
    }

    cancelAttempt() {
      this.generation += 1; // invalidates every in-flight callback for the old attempt
      this.viewerHost = null;
      this.clearTimers();
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      if (this.attemptAbort) {
        this.attemptAbort.abort();
        this.attemptAbort = null;
      }
    }

    setStatus(text) {
      if (this.statusEl) this.statusEl.textContent = text;
    }

    /* ---------------- gallery wiring ---------------- */

    findRefs() {
      const id = this.dataset.modelId;
      if (!id) return null;
      const product = this.closest('.js-product') || document;
      const gallery = product.querySelector('media-gallery');
      if (!gallery) return null;
      const item = gallery.querySelector(`[data-media-id="${escapeId(id)}"]`);
      const model = item ? item.querySelector('product-model') : null;
      if (!item || !model) return null;
      return { gallery, item, model };
    }

    select(refs) {
      // customSetActiveMedia is the real gallery method; setActiveMedia is a no-op stub.
      if (!refs || typeof refs.gallery.customSetActiveMedia !== 'function') return false;
      try {
        refs.gallery.customSetActiveMedia(refs.item, true);
        return true;
      } catch (e) {
        return false;
      }
    }

    /* ---------------- state machine ---------------- */

    activate() {
      const refs = this.findRefs();
      const selected = this.select(refs); // always reselect the model item, even when already ready
      if (this.state === 'loading') return; // rapid repeats never duplicate content
      if (this.state === 'ready' && this.viewer && this.viewer.isConnected) {
        if (this.panel) this.panel.hidden = false;
        this.setStatus('3D model ready.');
        return;
      }
      this.beginLoad(refs, selected);
    }

    async beginLoad(initialRefs, alreadySelected) {
      this.cancelAttempt();
      const gen = (this.generation += 1);
      this.state = 'loading';
      this.attemptAbort = new AbortController();
      const { signal } = this.attemptAbort;
      const deadline = Date.now() + this.timeout;

      if (this.panel) this.panel.hidden = true;
      if (this.openBtn) {
        this.openBtn.setAttribute('aria-expanded', 'true');
        this.openBtn.setAttribute('aria-busy', 'true');
      }
      this.setStatus('Loading 3D model…');
      this.setTimer(() => this.fail(gen), this.timeout);

      let refs = initialRefs || this.findRefs();
      if (!refs) {
        this.fail(gen);
        return;
      }

      // Bounded wait for the theme's custom elements to upgrade. model-viewer is
      // deliberately NOT awaited here: loadContent() itself triggers the Shopify
      // feature load that may register it.
      const upgraded = await this.waitFor(
        [customElements.whenDefined('product-model'), customElements.whenDefined('media-gallery')],
        deadline - Date.now()
      );
      if (gen !== this.generation) return;
      if (!upgraded) {
        this.fail(gen);
        return;
      }

      refs = this.findRefs() || refs;
      // Early clicks: the gallery had not upgraded yet, so issue the selection now.
      if (!alreadySelected) this.select(refs);

      const model = refs.model;
      let viewer = model.querySelector('model-viewer');

      if (viewer && viewer.loaded === true) {
        this.succeed(gen, model, viewer);
        return;
      }

      if (viewer) {
        // Existing but unloaded viewer (previous failure): attach first, then force a
        // genuine new request by clearing and restoring src across a frame boundary.
        this.attachViewer(gen, model, viewer, signal, deadline);
        this.recycleSrc(gen, viewer);
        return;
      }

      this.watchForViewer(gen, model, signal, deadline);

      if (model.getAttribute('loaded') !== null) {
        // Template was cloned but the viewer is gone; clear the guard so the
        // native loadContent path really rebuilds it.
        model.removeAttribute('loaded');
      }

      try {
        const poster = model.querySelector('.js-load-media');
        if (poster) poster.click();
        else if (typeof model.loadContent === 'function') model.loadContent();
        else throw new Error('no loader');
      } catch (e) {
        this.fail(gen);
        return;
      }

      // DeferredMedia clones its template synchronously, so the viewer is usually here now.
      viewer = model.querySelector('model-viewer');
      if (viewer) this.adoptViewer(gen, model, viewer, signal, deadline);
    }

    waitFor(promises, remaining) {
      if (!(remaining > 0)) return Promise.resolve(false);
      return Promise.race([
        Promise.all(promises).then(() => true),
        new Promise((resolve) => this.setTimer(() => resolve(false), remaining))
      ]);
    }

    watchForViewer(gen, model, signal, deadline) {
      if (typeof MutationObserver !== 'function') return;
      this.observer = new MutationObserver(() => {
        if (gen !== this.generation) return;
        const viewer = model.querySelector('model-viewer');
        if (!viewer) return;
        this.adoptViewer(gen, model, viewer, signal, deadline);
      });
      this.observer.observe(model, { childList: true, subtree: true });
    }

    adoptViewer(gen, model, viewer, signal, deadline) {
      if (gen !== this.generation || this.viewerHost === viewer) return;
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      this.attachViewer(gen, model, viewer, signal, deadline);
    }

    attachViewer(gen, model, viewer, signal, deadline) {
      this.viewerHost = viewer;
      // model-viewer load/error events do not bubble, so listen on the viewer itself.
      viewer.addEventListener('load', () => this.succeed(gen, model, viewer), { signal });
      viewer.addEventListener('error', () => this.fail(gen), { signal });
      if (viewer.loaded === true) {
        this.succeed(gen, model, viewer);
        return;
      }
      // model-viewer may upgrade after insertion; re-check `loaded` once it does.
      this.waitFor([customElements.whenDefined('model-viewer')], deadline - Date.now())
        .then((defined) => {
          if (!defined || gen !== this.generation) return;
          if (viewer.loaded === true) this.succeed(gen, model, viewer);
        });
    }

    recycleSrc(gen, viewer) {
      const src = viewer.getAttribute('src');
      if (!src) return;
      viewer.removeAttribute('src');
      const restore = () => {
        if (gen !== this.generation || !viewer.isConnected) return;
        viewer.setAttribute('src', src);
      };
      if (typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(() => this.setTimer(restore, 0));
      } else {
        this.setTimer(restore, 0);
      }
    }

    succeed(gen, model, viewer) {
      if (gen !== this.generation) return;
      this.clearTimers();
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      this.state = 'ready';
      this.viewer = viewer;
      if (this.panel) this.panel.hidden = false;
      if (this.openBtn) {
        this.openBtn.textContent = 'View in 3D';
        this.openBtn.setAttribute('aria-expanded', 'true');
        this.openBtn.removeAttribute('aria-busy');
      }
      this.setStatus('3D model ready.');

      const defaultAngle = this.querySelector('[data-angle]');
      if (defaultAngle) this.applyAngle(defaultAngle);
      if (this.rotateBtn) {
        if (reducedMotion()) this.rotateBtn.hidden = true;
        this.rotateBtn.setAttribute('aria-pressed', 'false');
      }
      this.rotating = false;
      try { viewer.autoRotate = false; } catch (e) { /* non-upgraded viewer */ }

      this.pollNativeUI(gen, model, viewer, 0);
    }

    fail(gen) {
      if (gen !== this.generation) return;
      this.cancelAttempt(); // bumps generation: late events from this attempt are ignored
      this.state = 'error';
      this.viewer = null;
      this.viewerHost = null;
      if (this.panel) this.panel.hidden = true;
      if (this.openBtn) {
        this.openBtn.textContent = 'Retry 3D view';
        this.openBtn.setAttribute('aria-expanded', 'false');
        this.openBtn.removeAttribute('aria-busy');
      }
      this.setStatus('The 3D model could not load. Photos are still available — try again.');
    }

    pollNativeUI(gen, model, viewer, tries) {
      if (gen !== this.generation) return;
      const ui = model.modelViewerUI;
      if (ui && model.modelViewer === viewer && typeof ui.play === 'function') {
        try { ui.play(); } catch (e) { /* native UI is optional */ }
        return;
      }
      if (tries >= UI_POLL_TRIES) return;
      this.setTimer(() => this.pollNativeUI(gen, model, viewer, tries + 1), UI_POLL_INTERVAL);
    }

    /* ---------------- controls ---------------- */

    applyAngle(button) {
      const viewer = this.viewer;
      this.querySelectorAll('[data-angle]').forEach((choice) => {
        choice.setAttribute('aria-pressed', String(choice === button));
      });
      if (!viewer) return;
      try {
        viewer.cameraOrbit = button.dataset.angle;
        viewer.setAttribute('camera-orbit', button.dataset.angle);
        if (reducedMotion() && typeof viewer.jumpCameraToGoal === 'function') viewer.jumpCameraToGoal();
      } catch (e) { /* viewer not upgraded yet */ }
      this.setStatus(`${button.textContent.trim()} view.`);
    }

    toggleRotate(button) {
      if (reducedMotion()) {
        button.setAttribute('aria-pressed', 'false');
        this.stopRotation();
        return;
      }
      const on = button.getAttribute('aria-pressed') !== 'true';
      button.setAttribute('aria-pressed', String(on));
      this.rotating = on;
      if (!this.viewer) return;
      try {
        this.viewer.autoRotate = on;
        this.viewer.toggleAttribute('auto-rotate', on);
      } catch (e) { /* viewer not upgraded yet */ }
    }

    stopRotation() {
      this.rotating = false;
      if (this.rotateBtn) this.rotateBtn.setAttribute('aria-pressed', 'false');
      if (!this.viewer) return;
      try {
        this.viewer.autoRotate = false;
        this.viewer.removeAttribute('auto-rotate');
      } catch (e) { /* viewer not upgraded yet */ }
    }
  }

  if (!customElements.get('kns-product-3d-controls')) {
    customElements.define('kns-product-3d-controls', KnsProduct3dControls);
  }
})();
